import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AppConfigDTO, FolderDTO, ImageDTO } from "@shared/types";
import { MIME_EXTENSION } from "@shared/types";
import { useUpload } from "../hooks/useUpload";
import { formatBytes } from "../lib/format";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { UploadQueueItem } from "./UploadQueueItem";

interface Props {
  open: boolean;
  onClose: () => void;
  folders: FolderDTO[];
  defaultFolderId: string | null;
  initialFiles?: File[];
  config: AppConfigDTO | null;
  onUploaded: (img: ImageDTO) => void;
}

const DUP_LABEL: Record<string, string> = { copy: "Create copy", replace: "Replace", cancel: "Skip" };

function extFor(file: File): string {
  return MIME_EXTENSION[file.type] ?? (file.name.split(".").pop() || "bin").toLowerCase();
}

export function UploadDialog({ open, onClose, folders, defaultFolderId, initialFiles, config, onUploaded }: Props) {
  const up = useUpload(config, onUploaded);
  const [folderId, setFolderId] = useState<string>(defaultFolderId ?? "");
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const seeded = useRef(false);

  useEffect(() => {
    if (open) setFolderId(defaultFolderId ?? "");
  }, [open, defaultFolderId]);

  useEffect(() => {
    if (open && initialFiles && initialFiles.length && !seeded.current) {
      seeded.current = true;
      up.addFiles(initialFiles);
    }
    if (!open) {
      seeded.current = false;
      up.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFiles]);

  const accept = config?.acceptMime.join(",") ?? "image/*";
  const sortedFolders = useMemo(() => [...folders].sort((a, b) => a.path.localeCompare(b.path)), [folders]);

  const pickFiles = (list: FileList | null) => {
    if (list && list.length) up.addFiles(Array.from(list));
  };

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const files = Array.from(e.dataTransfer.files).filter(
        (f) => f.type.startsWith("image/") || /\.(jpe?g|png|webp|gif|avif|bmp|tiff?|ico|svg)$/i.test(f.name),
      );
      if (files.length) up.addFiles(files);
    },
    [up],
  );

  const total = up.items.length;
  const done = up.items.filter((i) => i.status === "done").length;
  const uploading = up.items.some((i) => i.status === "uploading");
  const queued = up.items.filter((i) => i.status === "queued").length;
  const failed = up.items.filter((i) => i.status === "error").length;
  const overallPct = total
    ? Math.round(up.items.reduce((s, i) => s + (i.status === "done" ? 100 : i.progress), 0) / total)
    : 0;

  const selectClass = "field-sm select-chevron w-full cursor-pointer";

  return (
    <Modal
      open={open}
      onClose={uploading ? () => {} : onClose}
      title="Upload images"
      size="lg"
      footer={
        <div className="flex w-full items-center justify-between">
          <span className="text-2xs tabular-nums text-zinc-500">
            {total > 0 &&
              (uploading || queued > 0
                ? `Uploading ${done + 1} of ${total}, ${overallPct}%`
                : `${done} uploaded${failed ? `, ${failed} failed` : ""}`)}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={uploading}>
              {done > 0 && queued === 0 && !uploading ? "Done" : "Close"}
            </Button>
            <Button
              variant="primary"
              disabled={queued === 0 && failed === 0}
              onClick={() => {
                up.items.filter((i) => i.status === "error").forEach((i) => up.retry(i.id));
                up.start(folderId || null);
              }}
            >
              {failed > 0 && queued === 0 ? "Retry failed" : `Upload${queued ? ` ${queued}` : ""}`}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-2xs font-medium uppercase tracking-wide text-zinc-500">
              Destination folder
            </span>
            <select value={folderId} onChange={(e) => setFolderId(e.target.value)} className={selectClass}>
              <option value="">Uncategorized (root)</option>
              {sortedFolders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.path}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-2xs font-medium uppercase tracking-wide text-zinc-500">Filename</span>
            <select
              value={up.filenameMode}
              onChange={(e) => up.setFilenameMode(e.target.value as typeof up.filenameMode)}
              className={selectClass}
            >
              <option value="as-is">Use the name I type</option>
              <option value="unique-id">Add a short unique id</option>
              <option value="unique-timestamp">Add a timestamp</option>
            </select>
          </label>
        </div>

        <div>
          <span className="mb-1.5 block text-2xs font-medium uppercase tracking-wide text-zinc-500">
            If a name already exists
          </span>
          <div className="glass glass-quiet inline-flex rounded-lg p-1">
            {(["copy", "replace", "cancel"] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => up.setDuplicateStrategy(s)}
                className={`rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${
                  up.duplicateStrategy === s
                    ? "bg-zinc-900 text-white shadow-raise"
                    : "text-zinc-600 hover:bg-white/70 hover:text-zinc-900"
                }`}
              >
                {DUP_LABEL[s]}
              </button>
            ))}
          </div>
        </div>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`grid cursor-pointer place-items-center rounded-xl border-2 border-dashed px-4 py-10 text-center transition-colors ${
            dragging ? "border-zinc-900 bg-white/70" : "border-zinc-900/15 bg-white/30 hover:border-zinc-900/30"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple
            className="hidden"
            onChange={(e) => {
              pickFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <p className="text-[13px] text-zinc-600">
            <span className="font-medium text-zinc-900">Choose files</span> or drop them here
          </p>
          <p className="mt-1 text-2xs text-zinc-500">Up to {formatBytes(config?.maxImageBytes ?? 26214400)} each</p>
        </div>

        {total > 0 && (
          <ul className="scrollable max-h-72 space-y-2 overflow-y-auto pr-1">
            {up.items.map((item) => (
              <UploadQueueItem
                key={item.id}
                item={item}
                extension={extFor(item.file)}
                showNameField
                onNameChange={(name) => up.setName(item.id, name)}
                onRetry={() => up.retry(item.id)}
                onRemove={() => up.remove(item.id)}
                onCancel={() => up.cancel(item.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
